import { ControlType, OptionsOfControlType } from "../controltype";
import { Lookup } from "../../lookup/lookup";
import { DtoState } from "../../types/shared-enums";
import { DefaultSettings } from "../../devexpress/settings/defaultsettings";
import { Lazy } from "../../core/lazy";
import { StringHelper } from "../../helper/string-helper";
import { StringExtend } from "../../extend/string-extend";
import { Logger } from "../../logger/logger";
import { KoExtend } from "../../extend/knockout-extend";

export interface IObservableFactoryOptions<TControlType extends ControlType, TModel, TModelKey extends keyof TModel> extends IFactoryOptions<TControlType> {
	key?: TModelKey | KnockoutObservable<any>;
}

export interface IFactoryOptions<TControlType extends ControlType> {
	type: TControlType;
	lookup?: ILookupOptions | Lookup<any>;
	options?: OptionsOfControlType<TControlType> | KnockoutObservable<OptionsOfControlType<TControlType>>;
}

interface ILookupOptions {
	provider: Lookup<any>;
	filter?: (value: any) => boolean;
	keyExpr?: string;
	includeArchived?: boolean;
	includeInactive?: boolean;
}

interface IFactoryContext {
	model: any;
	property: KnockoutObservable<any>;
	options: any;
	type: ControlType;
	lookup: ILookupOptions;
}

export interface IFactoryDefaultOptions {
	optionName: string;
	keys: string[];
	value: KnockoutObservable<any>;
}

export class DxOptionFactory<TModel> {
	public static getInstance = Lazy.singleton(DxOptionFactory);
	constructor(public readonly model?: KnockoutObservable<TModel>, public readonly defaultOptions?: IFactoryDefaultOptions[]) { }

	public createComputedOptions<TControlType extends ControlType, TModelKey extends keyof TModel>(o: IObservableFactoryOptions<TControlType, TModel, TModelKey>): KnockoutComputed<OptionsOfControlType<TControlType>> {
		if (!this.model && o.key != null && typeof !ko.isObservable(o.key)) {
			throw new Error("no model set for model key " + String(o.key));
		}

		return ko.computed(() => {
			let model = o.key && this.model();
			let propertyName = ko.isObservable(o.key) ? null : o.key as string;

			let property = ko.isObservable(o.key) ? o.key : (model && model[o.key] as any);
			let result = this.createOptionsInternal(o, model, property);
			//if (o.key === "isShortFall") console.log({ o, result });

			if (this.defaultOptions && this.defaultOptions.length > 0) {
				// check if key is in one option
				if (!StringExtend.isNullOrEmpty(propertyName)) {
					let options = this.defaultOptions.filter((def) => def.keys && def.keys.indexOf(propertyName) !== -1);
					let resultAny = result as any;
					for (let option of options) {
						if (!resultAny[option.optionName]) {
							resultAny[option.optionName] = option.value;
						}
					}
				} else {
					Logger.warn("No popertyName found for given defaultOptions", this.defaultOptions);
				}
			}

			return result;
		});
	}

	public createOptions<TControlType extends ControlType>(o: IFactoryOptions<TControlType>) {
		return this.createOptionsInternal(o, null, null);
	}

	private createOptionsInternal<TControlType extends ControlType>(o: IFactoryOptions<TControlType>, model: TModel, property: KnockoutObservable<any>): OptionsOfControlType<TControlType> {
		let lookupOptions = o.lookup instanceof Lookup ? { provider: o.lookup } : o.lookup;

		let context: IFactoryContext = {
			model,
			property,
			lookup: lookupOptions,
			type: o.type,
			options: this.getOptions(o.options),
		};

		if (context.model) {
			this.applyOptionsFromModel(context);
		}

		if (context.property && !(context.lookup && context.model.state() === DtoState.Empty)) {
			this.applyProperty(context);
		}

		if (context.lookup) {
			this.applyOptionsFromLookup(context);
		}

		return DefaultSettings.mergeWithDefault(context.type, context.options) as any;
	}

	private getOptions(options: any) {
		if (!options) {
			options = {};
		}
		if (ko.isObservable(options)) {
			options = options();
		}
		return $.extend(true, {}, options);
	}

	private applyOptionsFromModel(context: IFactoryContext) {
		switch (context.type) {
			case ControlType.List:
				context.options.selectedItemKeys = context.property;
				break;

			default:
				context.options.value = context.property;
				break;
		}

		if (context.model.state() === DtoState.ReadOnly) {
			switch (context.type) {
				case ControlType.Button:
					context.options.disabled = true;
					break;
				case ControlType.List:
					// to allow scrolling with the list still beeing disabled
					let dataSource = KoExtend.resolve(context.options.dataSource);
					if (Array.isArray(dataSource)) {
						let dataSourceShallowCopy = dataSource.map((val) => Object.assign({}, val))
						for (let item of dataSourceShallowCopy) {
							item.disabled = true;
						}
						context.options.dataSource = dataSourceShallowCopy;
						// select all option will be clickable otherwise
						if (context.options.selectionMode === "all") {
							context.options.selectionMode = "multiple";
						}
					} else {
						context.options.disabled = true;
					}
					break;
				default:
					context.options.readOnly = true;
					break;
			}
		} else if (context.options.readOnly == null) {
			context.options.readOnly = false;
		}

		if (context.model.state() === DtoState.Empty) {
			context.options.disabled = true;
		} else if (context.options.disabled == null) {
			context.options.disabled = false;
		}
	}

	private applyOptionsFromLookup(context: IFactoryContext) {
		let lookup = context.lookup;
		let options = context.options;
		let keyExpr = lookup.keyExpr || "id";
		let displayExpr = options?.displayExpr ?? "name";

		// filter archived and inactive items from lookup
		let filterArchivedAndInactive = (item: any) => {
			// if we include archived, skip filtering
			if (lookup.includeArchived && lookup.includeInactive) {
				return true;
			}

			let isNotArchived = (item["isArchived"] !== true && item["archived"] !== true);
			if (lookup.includeArchived) {
				isNotArchived = true;
			}
			let isActive = (item["isActive"] !== false);
			if (lookup.includeInactive) {
				isActive = true;
			}
			let propertyValue: any = null;

			if (ko.isObservable(context.property) || typeof context.property === "function") {
				propertyValue = context.property();
			} else {
				propertyValue = context.property;
			}

			let isArray = Array.isArray(propertyValue);
			let hasValue = false;
			if (isArray) {
				hasValue = (propertyValue as any[]).indexOf(item[keyExpr]) !== -1;
			} else {
				hasValue = propertyValue === item[keyExpr];
			}

			let showItem = (isActive && isNotArchived) || hasValue;
			return showItem;
		};

		let dataSource: any;
		if (lookup.filter && context.type !== ControlType.Lookup) {
			dataSource = ko.computed(() => lookup.provider.items().filter(filterArchivedAndInactive).filter(lookup.filter)).extend({ rateLimit: 100 });
		} else if (context.type === ControlType.Lookup) {
			dataSource = lookup.filter ? lookup.provider.items().filter(lookup.filter) : lookup.provider.items();
		} else {
			dataSource = ko.computed(() => lookup.provider.items().filter(filterArchivedAndInactive)).extend({ rateLimit: 100 });
		}

		switch (context.type) {
			case ControlType.List:
				options.keyExpr = keyExpr;
				options.itemTemplate = (itemData: any, itemIndex: number, itemElement: any) => {
					itemElement.append(`<b>${itemData[displayExpr]}</b>`);
				};
				options.dataSource = dataSource;
				options.searchExpr = options?.searchExpr ?? displayExpr;
				break;
			case ControlType.TextBox:
				if (context.property != null) {
					let propertyValue = context.property();
					let newValue = lookup.provider.items().find((value) => propertyValue === value[keyExpr]);
					if (newValue) {
						options.value = newValue.name;
					} else {
						options.value = "";
					}
				}
				break;
			default:
				if (options.displayExpr == null) { options.displayExpr = "name"; }
				options.valueExpr = keyExpr;
				options.dataSource = dataSource;
				break;
		}
	}

	private applyProperty(context: IFactoryContext) {
		switch (context.type) {
			case ControlType.List:
				context.options.selectedItemKeys = context.property;
				break;

			default:
				context.options.value = context.property;
				break;
		}
	}
}